"use client";
import { useEffect, useMemo } from "react";
import { useSourceBasedStore } from "../../store/sourceStore";
import type { CategoryConfig } from "../../types/source.types";
import { FieldGroup } from "./SourceFieldGroup";
import { usePublicationsInfinite } from "../../api/source.queries";

type Props = {
  category: CategoryConfig;
  search?: string;
};

export function MediaTabContent({ category, search }: Props) {
  const setDefaultExpanded = useSourceBasedStore((s) => s.setDefaultExpanded);

  useEffect(() => {
    const subcategoryIds = category.subcategories.map((subcat) => subcat.id);
    setDefaultExpanded(subcategoryIds);
  }, [category.subcategories, setDefaultExpanded]);

  // Fetch data for all subcategories to check if there are any results
  const subcategoryQueries = category.subcategories.map((subcat) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    usePublicationsInfinite(subcat, search),
  );

  // Check if any subcategory has results when searching
  const hasAnyResults = useMemo(() => {
    if (!search?.trim()) return true;

    return subcategoryQueries.some((query) => {
      const items = (query.data?.pages ?? []).flatMap((p) => p.items);
      return items.length > 0;
    });
  }, [search, subcategoryQueries]);

  const isSearching = useMemo(() => {
    if (!search?.trim()) return false;
    return subcategoryQueries.some(
      (query) => query.isLoading || query.isFetching,
    );
  }, [search, subcategoryQueries]);

  return (
    <div className="space-y-5 overflow-x-hidden">
      {search?.trim() && (
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-neutral-800">
            Search Results: {search}
          </h3>
        </div>
      )}

      {category.subcategories.map((subcat) => (
        <FieldGroup key={subcat.id} subcat={subcat} search={search} />
      ))}

      {search?.trim() && !hasAnyResults && !isSearching && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="mb-2 text-4xl text-neutral-300">
            <i className="ri-search-line"></i>
          </div>
          <h3 className="mb-1 text-lg font-semibold text-neutral-700">
            No results found
          </h3>
          <p className="text-sm text-neutral-500">
            Try adjusting your search terms or browse all sources
          </p>
        </div>
      )}
    </div>
  );
}
